import { useRef, useEffect, forwardRef, useImperativeHandle, useState, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Draw, Clear } from "@mui/icons-material";

interface SignaturePadProps {
	label: string;
	clearButtonText: string;
	helperText?: string;
	error?: boolean;
	errorText?: string;
	initialSignature?: string;
	onSignatureChange?: (signature: string | null) => void;
	disabled?: boolean;
}

export interface SignaturePadRef {
	clear: () => void;
	getSignature: () => string | null;
	isEmpty: () => boolean;
}

const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
	(
		{
			label,
			clearButtonText,
			helperText,
			error,
			errorText,
			initialSignature,
			onSignatureChange,
			disabled = false,
		},
		ref
	) => {
		const signatureRef = useRef<SignatureCanvas>(null);
		const containerRef = useRef<HTMLDivElement>(null);
		const [canvasSize, setCanvasSize] = useState({ width: 400, height: 150 });

		// Resize canvas to match container
		const resizeCanvas = useCallback(() => {
			if (containerRef.current && signatureRef.current) {
				const rect = containerRef.current.getBoundingClientRect();
				const width = Math.floor(rect.width);
				const height = 150;

				const canvas = signatureRef.current.getCanvas();
				if (canvas) {
					canvas.width = width;
					canvas.height = height;
					canvas.style.width = `${width}px`;
					canvas.style.height = `${height}px`;
					setCanvasSize({ width, height });
					signatureRef.current.clear();
				}
			}
		}, []);

		// Initialize canvas size
		useEffect(() => {
			const timer = setTimeout(() => {
				resizeCanvas();
			}, 100);

			const resizeObserver = new ResizeObserver(() => {
				resizeCanvas();
			});

			if (containerRef.current) {
				resizeObserver.observe(containerRef.current);
			}

			return () => {
				clearTimeout(timer);
				resizeObserver.disconnect();
			};
		}, [resizeCanvas]);

		// Load initial signature if available
		useEffect(() => {
			if (initialSignature && signatureRef.current) {
				signatureRef.current.fromDataURL(initialSignature, {
					width: canvasSize.width,
					height: canvasSize.height,
				});
			}
		}, [initialSignature, canvasSize.width, canvasSize.height]);

		const handleClear = () => {
			if (signatureRef.current) {
				signatureRef.current.clear();
				onSignatureChange?.(null);
			}
		};

		const handleEnd = () => {
			if (signatureRef.current && !signatureRef.current.isEmpty()) {
				const dataUrl = signatureRef.current.toDataURL("image/png");
				onSignatureChange?.(dataUrl);
			}
		};

		// Expose methods to parent
		useImperativeHandle(ref, () => ({
			clear: handleClear,
			getSignature: () => {
				if (signatureRef.current && !signatureRef.current.isEmpty()) {
					return signatureRef.current.toDataURL("image/png");
				}
				return null;
			},
			isEmpty: () => signatureRef.current?.isEmpty() ?? true,
		}));

		return (
			<div className="w-full">
				<div className="flex items-center gap-2 mb-2">
					<Draw className="w-5 h-5 text-gray-600" />
					<label className="text-sm font-medium text-gray-700">{label}</label>
				</div>

				<div
					ref={containerRef}
					className={`relative border-2 rounded-lg overflow-hidden ${
						error ? "border-red-500" : "border-gray-300"
					} ${disabled ? "bg-gray-100 opacity-60" : "bg-white"}`}
					style={{ height: "150px" }}>
					<SignatureCanvas
						ref={signatureRef}
						penColor="black"
						minWidth={1.5}
						maxWidth={3}
						velocityFilterWeight={0.7}
						canvasProps={{
							width: canvasSize.width,
							height: canvasSize.height,
							className: `absolute top-0 left-0 touch-none ${disabled ? "pointer-events-none" : "cursor-crosshair"}`,
							style: {
								touchAction: "none",
							},
						}}
						backgroundColor="rgb(255, 255, 255)"
						onEnd={handleEnd}
					/>
				</div>

				<div className="flex justify-between items-center mt-2">
					{helperText && !error && <p className="text-xs text-gray-500">{helperText}</p>}
					{error && errorText && <p className="text-xs text-red-500">{errorText}</p>}
					{!disabled && (
						<button
							type="button"
							onClick={handleClear}
							className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors ml-auto">
							<Clear className="w-4 h-4" />
							{clearButtonText}
						</button>
					)}
				</div>
			</div>
		);
	}
);

SignaturePad.displayName = "SignaturePad";

export default SignaturePad;

